import {
  Lock,
  Unlock,
  X,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  FileText,
  Video,
  Info,
} from "lucide-react";
// 176 §1.1a — `import React` is the house remedy for react/react-in-jsx-scope
// (App.tsx, Dashboard.tsx, SettingsView.tsx, CostsView.tsx and six others
// already carry it). The eslint ratchet counts that rule PER JSX ELEMENT, so
// adding InfoHint's two elements to a file with no React import raised the
// file's count; the import is the cost of the addition, not a cleanup errand.
import React from "react";
import { useState, useEffect, useLayoutEffect, useRef, useCallback, useContext, Children, Fragment, isValidElement } from "react";
import type { CSSProperties, ReactNode, MutableRefObject } from "react";
import { I, coIni, fc } from "../utils";
import { API_URL, authHeaders } from "../api";
import { useTranslation, tImperative } from "../i18n";
import { RAD, FS, modalCap, Z } from "../lib/tokens";
import { PaneOpenContext, LiftedLevelContext, paneRungFor, useAbovePane } from "../lib/paneStacking"; // 204 §1; 205 §5 — a lifted shell owns the Escape
import { BADGE_SELF_RING } from "../lib/badgeStack"; // 118 §1
import type { Translate } from "../lib/interestRefusal"; // 194D §0.5 — the drawer shell's close word
import type { OnUse, RenderOnUse } from "../lib/loadOnUse.mjs"; // 208 §1 — LoadedOnUse's loader and what it draws
import { ListLoadContext } from "../lib/listLoad"; // 210 §1 — the lists that could not load, for ListNotLoaded

/**
 * Reusable UI components extracted from inline patterns across the app.
 * Usage: import { LockButton, MyAllToggle, SegmentedControl, InitialsBadge, AddButton, SaveButton, CancelButton, ModalLayout } from '../components/SharedUI';
 */

// --- openDocHref ---
// Phase 29 round 13 (C72) — guarded open for task-document links. A broken
// link must NEVER navigate the app:
//   • http(s) (and bare www.) targets open in a new tab — external access is
//     governed by M365, not LexCRM;
//   • app-internal/relative targets ("/…") are the only case where the app
//     can DETECT the viewer may lack access → "ask the Responsible Partner"
//     toast instead of opening;
//   • anything else (file paths, free text, javascript:…) → «not valid»
//     toast. Trilingual via tImperative; window.lxToast is the App's toast.
// Returns true when a tab was actually opened.
export function openDocHref(href: any) {
  const raw = String(href || "").trim();
  const say = (m: any) => {
    try {
      if (typeof window !== "undefined" && window.lxToast)
        window.lxToast(m, "warning");
    } catch {
      /* toast must never throw */
    }
  };
  if (/^https?:\/\//i.test(raw)) {
    window.open(raw, "_blank", "noopener");
    return true;
  }
  if (/^www\./i.test(raw)) {
    window.open("https://" + raw, "_blank", "noopener");
    return true;
  }
  if (raw.startsWith("/")) {
    say(
      tImperative("task.doc.not_allowed", {
        fallback:
          "You are not allowed to open this link — ask the Responsible Partner",
      }),
    );
    return false;
  }
  say(
    tImperative("task.doc.invalid_link", {
      name: raw,
      fallback: `The link "${raw}" is not valid`,
    }),
  );
  return false;
}

// --- LockButton ---
// Amber-glow lock/unlock toggle used in CaseView (parties, agenda, gendocs), ScheduleView, etc.
// Props: locked (bool), onToggle (fn), title? (string), style? (object)
/* 190 round 6 [AA] (ruled 2026-09-16 — "SAME behavior as the locks, across
   the app") — THE OPEN-MODE GLOW, the lock's own three declarations while
   UNLOCKED, plus its icon colour, declared ONCE so the lock and every
   Eisenhower / Bento icon read the same bytes and cannot drift. The
   semantics carry over unchanged: it pulses while you are IN the mode, and
   it is the way out. `lkp` is App.css's keyframe (opacity 1 → .45 → 1). */
export const OPEN_MODE_GLOW = {
  background: "rgba(251,146,60,.13)",
  border: "1px solid rgba(251,146,60,.35)",
  animation: "lkp 2s ease-in-out infinite",
} as const;
export const OPEN_MODE_ICON = "var(--or)";

export function LockButton({ locked, onToggle, title, style }: { locked?: any; onToggle?: any; title?: any; style?: any }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      className="link-btn"
      title={title || (locked ? t("ui.locked") : t("ui.editing"))}
      style={{
        display: "flex",
        alignItems: "center",
        background: locked ? "none" : OPEN_MODE_GLOW.background,
        border: locked
          ? "1px solid transparent"
          : OPEN_MODE_GLOW.border,
        borderRadius: RAD.r6,
        cursor: "pointer",
        padding: "2px 4px",
        lineHeight: 1,
        animation: locked ? undefined : OPEN_MODE_GLOW.animation,
        ...style,
      }}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        onToggle();
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {locked ? (
        <Lock size={I.m} style={{ color: "var(--ts)" }} />
      ) : (
        <Unlock size={I.m} style={{ color: OPEN_MODE_ICON }} />
      )}
    </button>
  );
}

// --- MyAllToggle ---
// Split "My / All" toggle pill used in Dashboard, CaseView, AgendaView, etc.
// Props: my (bool), onMy (fn), onAll (fn), style? (object)
export function MyAllToggle({ my, onMy, onAll, counts, style }: { my?: any; onMy?: any; onAll?: any; counts?: any; style?: any }) {
  const { t } = useTranslation();
  const countSpan = (n: any) =>
    n !== undefined ? (
      <span
        style={{
          fontSize: FS.s9,
          fontWeight: 400,
          color: "var(--tm)",
          marginLeft: 3,
        }}
      >
        {n}
      </span>
    ) : null;
  return (
    <div
      style={{
        display: "inline-flex",
        borderRadius: RAD.r20,
        overflow: "hidden",
        border: "1px solid var(--br)",
        ...style,
      }}
    >
      <button
        type="button"
        className="btn"
        style={{
          padding: "2px 10px",
          fontSize: FS.s10,
          borderRadius: RAD.r0,
          border: "none",
          background: my ? "var(--as)" : "var(--bc)",
          color: my ? "var(--ac)" : "var(--tm)",
          fontWeight: 600,
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onMy();
        }}
      >
        {t("toggle.my")}
        {countSpan(counts?.my)}
      </button>
      <button
        type="button"
        className="btn"
        style={{
          padding: "2px 10px",
          fontSize: FS.s10,
          borderRadius: RAD.r0,
          border: "none",
          borderLeft: "1px solid var(--br)",
          background: !my ? "var(--as)" : "var(--bc)",
          color: !my ? "var(--ac)" : "var(--tm)",
          fontWeight: 600,
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onAll();
        }}
      >
        {t("toggle.all")}
        {countSpan(counts?.all)}
      </button>
    </div>
  );
}

// --- SegmentedControl ---
// 188 (2) [RULED] — RENAMED from `PeriodFilter`, in place, nothing else
// changed: the name was wrong at most of its 22 mounts (prospect filters,
// EntityList's toolbar chips, a tasks filter, TimeEntryTable's tab strip —
// only a few are periods). It is the tab-shaped piece that already exists.
// Multi-option toggle pill used for week/period filtering.
// Props: value (string), options (array of [key, label]), onChange (fn(key)), style? (object)
// Example options: [["all","All"],["last","Last"],["this","This week"],["next","Next"]]
/* 151C §4 — `pulseKey`: the tab whose slice just received rows wears the
   one-shot amber pulse class while the host holds the key set; every other
   mount passes nothing and renders byte-identically. */
export function SegmentedControl({ value, options, onChange, counts, style, pulseKey }: { value?: any; options: any[]; onChange?: any; counts?: any; style?: any; pulseKey?: string | null }) {
  return (
    <div
      style={{
        display: "inline-flex",
        borderRadius: RAD.r20,
        overflow: "hidden",
        border: "1px solid var(--br)",
        ...style,
      }}
    >
      {options.map(([k, l], i) => {
        const count = counts ? counts[k] : undefined;
        const dimmed = counts && count === 0 && value !== k;
        return (
          <button
            key={k}
            type="button"
            className={pulseKey === k ? "btn teTabPulseAm" : "btn"}
            style={{
              padding: "2px 10px",
              fontSize: FS.s10,
              borderRadius: RAD.r0,
              border: "none",
              borderLeft: i > 0 ? "1px solid var(--br)" : "none",
              background: value === k ? "var(--as)" : "var(--bc)",
              color: value === k ? "var(--ac)" : "var(--tm)",
              fontWeight: 600,
              opacity: dimmed ? 0.4 : 1,
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onChange(k);
            }}
          >
            {l}
            {count !== undefined && (
              <span
                style={{
                  fontSize: FS.s9,
                  fontWeight: 400,
                  color: "var(--tm)",
                  marginLeft: 3,
                }}
              >
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// --- InitialsBadge ---
// Round collaborator initials badge. Phase 8 B3+B4 — single shared background
// `--cb` (admin-tunable in Theme Settings); self-affordance is a 1.5px `--ac`
// ring (box-shadow so it doesn't shift box dimensions), never a different fill.
// Props: person (object with firstName/lastName), isMe (bool), size? (number, default 20), fontSize? (number, default 9), style? (object)
// Phase 13 — an explicit admin-set `initials` field wins over the derived
// firstName[0]+lastName[0] so the badge matches the email-PDF filename SLOT.
//
// Phase 31 (C5) — PHOTO-CAPABLE: when `person.id` resolves to an M365
// profile photo (GET /api/avatars/:id, viewer token), the badge renders the
// photo in the same circle; initials remain the fallback (404 / staging stub
// / pre-consent token). NO upload surface — M365 owns the photo. The cache
// is module-level and session-long: one fetch per collaborator id, misses
// negative-cached, object URLs shared by every badge instance.
const _avatarCache = new Map(); // coId → 'none' | objectURL | Promise

function _loadAvatar(coId: any) {
  return fetch(`${API_URL}/avatars/${coId}`, {
    // 174 §1.2 — a BLOB response, so the bare fetch stays and spreads what
    // api() spreads. The comment here said "the Bearer stays for header
    // callers", which reads as "a browser has none": a browser holds the
    // cookie and, if its session predates 167 §2b, the Bearer too. Both go.
    credentials: "same-origin",
    headers: authHeaders(),
  })
    .then((res) => (res.ok ? res.blob() : null))
    .then((blob) => (blob && blob.size ? URL.createObjectURL(blob) : null))
    .catch(() => null);
}

export function InitialsBadge({
  person,
  isMe,
  size = 20,
  fontSize = 9,
  style,
  noDim,
  /* BRIEF 118 §3 — SUPPRESS THE NATIVE NAME TOOLTIP. 117 §3 replaced the badge
     area's comma-joined `title` with a structured hover panel and said "not a
     native title"; the per-badge one this component sets survived, so a badge
     inside a panel fired BOTH — Laurent's screenshot is the dark "Test
     ASSOCIATE" bubble drawn on top of the panel's "TAA — Test ASSOCIATE".
     Passed by the three stacks that sit inside `BadgeHoverArea`, and by
     nothing else: a badge with no panel keeps its tooltip, which is the only
     way its name can be read at all.
     It removes the ATTRIBUTE rather than emptying it, deliberately: an empty
     `title` suppresses the ancestor's too, and the ancestor's is the affordance
     ("Click to reassign") that 117 §3 ruled stays. */
  hideTitle,
  ...rest
}: { person?: any; isMe?: any; size?: number; fontSize?: number; style?: any; noDim?: boolean; hideTitle?: boolean; [k: string]: any }) {
  const pid = person && person.id != null ? person.id : null;
  // 75 §3 (A.8) — an ARCHIVED person renders greyed in every record that
  // already names them, decided HERE so no call site can forget it. The one
  // ruled exception is time entries ("the firm still invoices them"), which
  // opt out with `noDim`. Rows without a status (partial objects, pre-
  // migration cache) are untouched, so nothing changes for anyone active.
  const dimmed = !noDim && person && person.status === "archived";
  const dimStyle = dimmed ? { opacity: 0.45, filter: "grayscale(1)" } : null;
  const [photo, setPhoto] = useState<string | null>(() => {
    const c = pid != null ? _avatarCache.get(pid) : null;
    return typeof c === "string" && c !== "none" ? c : null;
  });
  useEffect(() => {
    if (pid == null) return;
    const cached = _avatarCache.get(pid);
    if (cached === "none") return;
    if (typeof cached === "string") {
      setPhoto(cached);
      return;
    }
    let dead = false;
    const pr =
      cached instanceof Promise
        ? cached
        : (() => {
            const p = _loadAvatar(pid);
            _avatarCache.set(pid, p);
            return p;
          })();
    pr.then((url) => {
      _avatarCache.set(pid, url || "none");
      if (!dead && url) setPhoto(url);
    });
    return () => {
      dead = true;
    };
  }, [pid]);
  // Phase 29 C63 — single source: utils.coIni (stored initials win, derived
  // firstName[0]+lastName[0] fallback) — byte-identical to the old inline rule.
  const initials = coIni(person);
  const fullName = [person?.firstName, person?.lastName]
    .filter(Boolean)
    .join(" ");
  if (photo) {
    return (
      <img
        src={photo}
        alt={initials}
        title={hideTitle ? undefined : fullName}
        style={{
          display: "inline-flex",
          width: size,
          height: size,
          borderRadius: RAD.pill,
          objectFit: "cover",
          boxShadow: isMe ? BADGE_SELF_RING : "none",
          flexShrink: 0,
          ...dimStyle,
          ...style,
        }}
        {...rest}
      />
    );
  }
  return (
    <span
      title={hideTitle ? undefined : fullName}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: size,
        height: size,
        borderRadius: RAD.pill,
        background: "var(--cb)",
        color: "var(--bf)",
        fontSize,
        fontWeight: 700,
        // Phase 9 Item 6 — the `1px solid var(--br)` border was a Phase 8
        // regression: a light-grey ring sandwiched between the `--cb` fill
        // and the `--ac` self-affordance ring, giving the current-user badge
        // a fourth visible layer. Removed so the badge matches its documented
        // design — `--cb` fill, `--bf` text, and (for isMe only) the `--ac`
        // ring. Non-current-user badges are fill + text, no ring.
        boxShadow: isMe ? BADGE_SELF_RING : "none",
        flexShrink: 0,
        ...dimStyle,
        ...style,
      }}
      {...rest}
    >
      {initials}
    </span>
  );
}

// --- ArchivedHoleBadge ---
// 75 §3 (A.8) — the person-shaped hole. A record whose ONLY person was
// archived is earmarked with a badge carrying `?` — deliberately the same
// appearance as every other initials badge (a badge, not a dot), rendered in
// the place the people render, so the record reads as "a person was here and
// is gone" rather than as owned by nobody. Rendered BESIDE the greyed badge
// of whoever it was, never instead of it — the identity is ruled to stay.
// Never rendered for time entries. `person.id: null` skips the avatar fetch;
// explicit `initials` win over the derived rule (both measured, ruling A.8).
export function ArchivedHoleBadge({ size = 20, fontSize = 9, style }: { size?: number; fontSize?: number; style?: object }) {
  const { t } = useTranslation();
  return (
    <InitialsBadge
      person={{ initials: "?", id: null }}
      size={size}
      fontSize={fontSize}
      style={style}
      title={t("co.archived_sole_hover", {
        fallback: "The only person on this record has been archived",
      })}
    />
  );
}

// --- SortCaret ---
// 200-glance §M — THE APP'S ONE SORT CARET, extracted. Written FROM the three
// sites that already held the space and were byte-identical to each other:
// `InvoiceTable.tsx:229-236` (`sortInd`), `LibraryView.tsx:328-335` (`thArrow`),
// `ReportsView.tsx:1714-1723` (`sortInd`, which keeps its own `if (!k) return
// null` guard above the call). The markup below is theirs VERBATIM: the sorted
// column's `▲` / `▼` at `marginLeft: 3`, `FS.s8`; every other sortable column
// the dim `▶` at the same margin and size, `opacity: 0.35` — so a heading is
// the same width sorted or not. The two things every site varied became the
// props: whether this column is the sorted one, and the direction (`1`
// ascending, as every `{ k, d }` sort state in the app spells it).
//
// Why one definition: with `table-layout: auto` (App.css) a caret that appears
// ONLY on the sorted column widens that heading by a glyph and the browser
// re-solves every column width — Laurent saw the activity tables' headings
// re-wrap, and their neighbours', on every sort. The five sites that rendered
// NOTHING when unsorted (CostsView's three, EntityList's two — one of them at
// `marginLeft: 2`) were converted in M.2, the improvement, its own commit —
// and two of them can hold NO sort at all (an activity table before its first
// press, a list with no stored sort), so `d` is optional: the union of what
// the eight sites pass. An inactive caret reads no direction.
export function SortCaret({ active, d }: { active: boolean; d?: number }) {
  return active ? (
    <span style={{ marginLeft: 3, fontSize: FS.s8 }}>{d === 1 ? "▲" : "▼"}</span>
  ) : (
    <span style={{ marginLeft: 3, fontSize: FS.s8, opacity: 0.35 }}>▶</span>
  );
}

// --- InfoHint ---
// 176 §1.1 — THE APP'S ONE INFO HOVER, extracted. There was exactly one:
// `CaseCalculatorSection.tsx:826-834 @2d1134f0` — the span §1.1b then replaces,
// so the coordinate is marked at the base commit, not at HEAD (censused: at
// 2d1134f0 `<Info size=` appears once in src/). The markup below is it
// VERBATIM; the only generalisation is that the two things its single call
// site varied — the sentence and the icon size — became props, and `size`
// defaults to the 10 it passed. This is an EXTRACTION, not an improvement: no
// aria role is invented, no tooltip library is imported. `cursor:"help"` and
// `var(--tm)` are the app's existing hover-help vocabulary (shared with
// TaxonomyOrphanMark) and stay exactly as they were.
//
// `title` is the browser's own tooltip, which is why this is one span: the
// app has no tooltip component and §1.1 rules that none be invented.
export function InfoHint({ title, size = 10 }: { title?: string; size?: number }) {
  return (
    <span title={title} style={{ color: "var(--tm)", cursor: "help" }}>
      <Info size={size} />
    </span>
  );
}

// --- JoinBtn ---
// Phase 31 (C4) — the Join affordance for agenda rows that carry a meeting
// join URL (Teams meetings born in the meeting modal, imported Outlook
// events with onlineMeetingUrl). Opens in a new tab, never triggers the
// row's own click. `compact` renders icon-only for dense rows.
export function JoinBtn({ url, stub, size = 11, compact = false, style }: { url?: any; stub?: boolean; size?: number; compact?: boolean; style?: any }) {
  const { t } = useTranslation();
  if (!url) return null;
  /* BRIEF 117 §6c — A STUB JOIN MUST NOT LOOK REAL. On staging there is no
     M365 tenant, so `graphStubMode()` mints `https://stub.local/teams/join/…`
     (m365.js) and the flow is smokable end to end — but the affordance opened a
     dead tab, which is the 108 §2a control-that-lies class wearing a link. The
     server answers the question rather than the client guessing at a URL shape:
     `joinUrlStub` is derived at shape time (routes/agenda.js). Rendered as a
     muted, non-navigating span that SAYS what it is; the ink stays so the
     smoke path still shows a meeting was minted. */
  if (stub)
    return (
      <span
        onClick={(e) => e.stopPropagation()}
        title={t("alerts.join_stub_tip", {
          fallback: "Staging stub — no Microsoft 365 tenant, so this meeting has no real join link",
        })}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 3,
          padding: compact ? "1px 4px" : "1px 7px",
          fontSize: FS.s9_5,
          fontWeight: 700,
          color: "var(--tm)",
          border: "1px dashed var(--br)",
          borderRadius: RAD.r5,
          flexShrink: 0,
          cursor: "default",
          ...style,
        }}
      >
        <Video size={size} />
        {!compact && t("alerts.join_stub", { fallback: "Join (stub)" })}
      </span>
    );
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener"
      onClick={(e) => e.stopPropagation()}
      title={t("alerts.join_meeting_tip", {
        fallback: "Join the online meeting",
      })}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 3,
        padding: compact ? "1px 4px" : "1px 7px",
        fontSize: FS.s9_5,
        fontWeight: 700,
        color: "var(--gn)",
        border: "1px solid var(--gn)",
        borderRadius: RAD.r5,
        textDecoration: "none",
        flexShrink: 0,
        ...style,
      }}
    >
      <Video size={size} />
      {!compact && t("alerts.join", { fallback: "Join" })}
    </a>
  );
}

// --- TaskDocLink ---
// Phase 29 C66 — compact document-link affordance for task rows: a FileText
// icon that NEVER truncates (callers place it OUTSIDE their ellipsis span,
// flex-none), opens the link in a new tab and never triggers the row's own
// navigation. No LexCRM-side gating: any viewer of the row may follow it.
// Props: href (string|null — renders nothing when falsy), size?, style?
export function TaskDocLink({ href, size = 12, style }: { href?: any; size?: number; style?: any }) {
  const { t } = useTranslation();
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener"
      title={t("task.doc_link_tip", { fallback: "Open linked document" })}
      onClick={(e) => e.stopPropagation()}
      style={{
        display: "inline-flex",
        alignItems: "center",
        flexShrink: 0,
        color: "var(--ac)",
        lineHeight: 0,
        ...style,
      }}
    >
      <FileText size={size} />
    </a>
  );
}

// --- AddButton ---
// Translucent blue "+ Add" / "+ Event" button.
// Props: label (string or node, e.g. "Event", "Add"), onClick (fn), style? (object)
//
// Brief 57 §2 — THE PLUS BELONGS HERE AND NOT TO THE TRANSLATIONS. Twelve of
// the fifteen label keys the 29 call sites use carried it INSIDE the translated
// text, in all three languages: 36 database rows repeating one character that
// is not a word and cannot be translated. `common.add` did not carry it, which
// is how the ruling was visible on screen — one add button without a plus,
// beside a dozen with one. The component supplies it now, so the question
// cannot be answered two ways again.
//
// It is UNCONDITIONAL, with no opt-out prop, because an opt-out is the branch
// that lets the two answers come back. Every one of the 29 sites is an add
// affordance, including the two folder-creation buttons whose label is an icon
// beside a phrase — those now read "+ 🗀 Create Case Folder".
// BRIEF 105 §1a — `disabled` IS A UNION MEMBER, NOT A NEW IDEA.
// The enrolment-invitation button joins this component (§1a: "now it's just
// regular text everywhere and one has to guess where to click"), and the button
// it replaces carried `disabled={busy}` — a request is in flight and a second
// click would mint a second invitation, REPLACING the outstanding one. Rule 2
// says a primitive takes the union of every prop every call site actually
// passes, and rule 4 says widen the primitive rather than bend the behaviour:
// dropping the guard to fit the component would have been the bend.
// Optional and absent by default, so the other 23 sites render byte-identically.
export function AddButton({ label, onClick, style, visible = true, disabled, title }: { label?: any; onClick?: any; style?: any; visible?: boolean; disabled?: boolean; title?: string }) {
  if (!visible) return null;
  return (
    <button
      type="button"
      className="btn btn-primary"
      style={{ padding: "2px 7px", fontSize: FS.s10, ...style }}
      onClick={onClick}
      disabled={disabled}
      title={title}
    >
      {"+ "}
      {label}
    </button>
  );
}

// --- SectionHeader ---
// Consistent section header row: title left, toggles next, flexible space, action buttons right.
// Props:
//   title (string|JSX) — section title text
//   id (string) — optional id for the header element
//   className (string) — optional className for the wrapper div
//   leftContent (JSX) — toggles like MyAllToggle, SegmentedControl placed after title
//   rightContent (JSX) — AddButton, LockButton, etc. pushed to far right
//   style (object) — extra style on wrapper div
//   collapsible (bool) — when true, renders a left-side chevron and the title
//     becomes the click target. Pair with `open` + `onToggle`.
//   open (bool) — chevron state; ignored if collapsible is false.
//   onToggle (fn) — chevron click handler. Always uses the canonical Phase 13
//     chevron component (ChevronRight collapsed / ChevronDown open, size 11).
/* 188 §F.2 [IMPROVEMENT, glance correction — Laurent 2026-09-14] — WHEN THE HEADER
   ROW IS TOO NARROW, THE FILTERS WRAP, NOT THE BUTTONS. The row is one
   flex-wrap line — chevron · title · the filters (leftContent) · the right
   group (`marginLeft: auto`: Add + lock) — and a flex line breaks at the LAST
   item that no longer fits, so on a narrow case column the filter chips kept
   the title row and "+ Ajouter" and the lock dropped to the next line. Flex
   cannot wrap the middle item first (line breaking follows order, and
   `order` would also move the filters after the buttons on a wide row), so the
   fit is MEASURED: the natural widths of the row's children — the chevron,
   the title and its 12px margin, each filter chip, the right group — plus the
   gaps, against the row's width, in layout px (`offsetWidth`, never
   `getBoundingClientRect`, which is visual px under the root zoom — 142's
   finding). When they do not fit, the filters wrapper takes the whole next
   line (`flexBasis: 100%`, `order: 2`) and the title row keeps the buttons.
   The arithmetic reads intrinsic widths only, so it does not depend on which
   state is rendered and cannot oscillate. Re-measured on every render (counts
   change chip widths) and on the row's own resize.
   ONE home, not per section: CaseTasksSection and CaseAgendaSection (mounted
   by CaseView AND CollectiveCaseView) are the sections the glance named;
   CollectiveCaseView's clients section is the third header that passes
   leftContent and gets the same behaviour. When everything fits, the wrapper
   is one flex item holding the chips at the row's own gap — the same pixels
   as the chips as direct children. */
const HDR_GAP = 5;
function useFiltersWrap(rowRef: { current: HTMLDivElement | null }, active: boolean) {
  const [wrap, setWrap] = useState(false);
  useLayoutEffect(() => {
    const row = rowRef.current;
    if (!active || !row) return undefined;
    const measure = () => {
      let need = 0;
      let items = 0;
      for (const el of Array.from(row.children) as HTMLElement[]) {
        if (el.dataset.hdrFilters !== undefined) {
          const chips = Array.from(el.children) as HTMLElement[];
          for (const c of chips) need += c.offsetWidth;
          need += HDR_GAP * Math.max(0, chips.length - 1);
        } else {
          // the row's only margins are inline (the title's 12px; the right
          // group's `auto`, which parses to 0 and is free space, not width)
          need += el.offsetWidth + (parseFloat(el.style.marginLeft) || 0) + (parseFloat(el.style.marginRight) || 0);
        }
        items += 1;
      }
      need += HDR_GAP * Math.max(0, items - 1);
      setWrap(need > row.clientWidth);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(row);
    return () => {
      ro.disconnect();
    };
  });
  return wrap;
}

export function SectionHeader({
  title,
  id,
  className,
  leftContent,
  rightContent,
  style,
  collapsible,
  open,
  onToggle,
}: {
  title?: any;
  id?: any;
  className?: any;
  leftContent?: any;
  rightContent?: any;
  style?: any;
  collapsible?: any;
  open?: any;
  onToggle?: any;
}) {
  const { t } = useTranslation();
  const cls = ["csSecHdr", className].filter(Boolean).join(" ");
  // Phase 25.1 spine: when a section is collapsed, the header shows only its
  // name + chevron. All controls (viewing toggles in leftContent, the lock and
  // action buttons in rightContent) appear only once the section is opened.
  // Non-collapsible headers always show their controls.
  const showControls = !collapsible || open;
  const rowRef = useRef<HTMLDivElement | null>(null);
  const filtersWrap = useFiltersWrap(rowRef, !!(showControls && leftContent));
  return (
    <div
      id={id}
      ref={rowRef}
      className={cls}
      style={{
        display: "flex",
        alignItems: "center",
        gap: HDR_GAP,
        margin: "10px 0 6px",
        flexWrap: "wrap",
        ...style,
      }}
    >
      {collapsible && (
        <button
          type="button"
          aria-expanded={!!open}
          title={
            open
              ? t("common.collapse", { fallback: "Collapse" })
              : t("common.expand", { fallback: "Expand" })
          }
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onToggle && onToggle();
          }}
          style={{
            background: "none",
            border: "none",
            padding: 0,
            margin: 0,
            cursor: "pointer",
            color: "var(--tm)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 14,
            height: 14,
            flexShrink: 0,
          }}
        >
          {open ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
        </button>
      )}
      <h3
        style={{
          fontSize: FS.s12,
          marginRight: 12,
          ...(collapsible ? { cursor: "pointer" } : null),
        }}
        onClick={
          collapsible
            ? (e) => {
                e.preventDefault();
                onToggle && onToggle();
              }
            : undefined
        }
      >
        {title}
      </h3>
      {showControls && leftContent && (
        <div
          data-hdr-filters=""
          style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: HDR_GAP,
            ...(filtersWrap ? { flexBasis: "100%", order: 2 } : null),
          }}
        >
          {leftContent}
        </div>
      )}
      {showControls && (
        <div
          style={{
            marginLeft: "auto",
            display: "flex",
            alignItems: "center",
            gap: 5,
          }}
        >
          {rightContent}
        </div>
      )}
    </div>
  );
}

// --- SaveButton ---
// Full-width modal save button (mfS) or danger/delete button (mfD).
// Props: label (string, defaults to translated "Save"), onClick (fn), icon (string, default "✓"), danger (bool, uses mfD), style? (object)
export function SaveButton({
  label,
  onClick,
  icon = "✓",
  danger = false,
  style,
  disabled = false,
  title,
}: {
  label?: any;
  onClick?: any;
  icon?: any;
  danger?: boolean;
  style?: any;
  disabled?: boolean;
  title?: any;
}) {
  const { t } = useTranslation();
  const lbl = label != null ? label : t("common.save", { fallback: "Save" });
  return (
    <button
      type="button"
      className={danger ? "modal-delete" : "modal-save"}
      style={style}
      disabled={disabled}
      title={title}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!disabled) onClick();
      }}
    >
      {icon && <span>{icon}</span>} {lbl}
    </button>
  );
}

// --- CancelButton ---
// Centered text-link cancel button for modal footers.
// Props: label (string, defaults to translated "Cancel"), onClick (fn)
export function CancelButton({ label, onClick }: { label?: any; onClick?: any }) {
  const { t } = useTranslation();
  const lbl =
    label != null ? label : t("common.cancel", { fallback: "Cancel" });
  return (
    <button type="button" className="modal-cancel" onClick={onClick}>
      {lbl}
    </button>
  );
}

// BRIEF 87 §9 — THE SCROLL HINT, ON EVERY SCROLLABLE MODAL [RULED].
//
// THE INCIDENT: TaskModal's TYPE field renders unconditionally
// (TaskModal.tsx:174) and was simply scrolled out of view, with NOTHING on
// screen saying so — and the reader concluded the field had been deleted. That
// is the whole argument.
//
// ── THE ENUMERATION §9 ASKED FOR, AND LAURENT'S HYPOTHESIS IS CONFIRMED ─────
//
// "I think they all use the same model at least for GUI" — measured, and yes:
// `.modal-body` (App.css:854-859, `overflow-y: auto; flex: 1 1 auto;
// min-height: 0`) is THE single scroll region in this application, and every
// modal reaching it goes through ModalLayout below. 26 files consume
// ModalLayout; `Mod.tsx` was a thin wrapper over it and added nothing — 188 A.4
// deleted it and its 19 mounts call ModalLayout directly.
//
// ONE SHELL MEANS ONE CHANGE, so this is that one change — and it needs no
// decision, which is why §15's "the modals do not share one shell → STOP" does
// not fire.
//
// THE EXCEPTION LIST IS EIGHT CALL SITES, all passing `rawBody`, which renders
// children INSTEAD of wrapping them in `.modal-body` and therefore has no
// scroll region to hint at: App.tsx:7780 · :14125 · :14318 · :14423 · :14921,
// Dashboard.tsx:1342, Phase4SettingsPanels.tsx:599, useConfirmAndUndo.tsx:124.
// Each owns its own body, so each owns its own scrolling; the hint deliberately
// does not reach them and this comment is the record of which eight.
// 188 §A — nineteen more since: the hand-rolled shells converted onto
// ModalLayout (App.tsx's five, the two collective modals, the OneDrive picker,
// the Outlook import, the two Phase4 panels, the two dialogs, CollectiveCaseView's
// cc modal, InvoiceView's doubles, LibraryView's three, SettingsView's preview)
// all pass `rawBody` and keep their own body — none of them ever had the hint,
// and a cure renders the same pixels. Giving them the hint is a ruled act.
//
// ── WHY STICKY, ZERO-HEIGHT SPANS AND NOT A WRAPPER ────────────────────────
//
// It must not take layout space (§9) and it must not be a scrollbar ("this is
// not for navigating, just a subtle hint that the scroll doesn't end there").
// A wrapper div around `.modal-body` would have meant moving `flex: 1 1 auto`
// and `min-height: 0` onto the new element and changing the DOM shape of every
// modal in the app. Two `position: sticky` spans of `height: 0`, as the first
// and last children of the existing scroller, need none of that: sticky keeps
// them pinned to the scroller's own edges as it scrolls, and zero height means
// the content below is exactly where it was to the pixel.
//
// `pointer-events: none` so it can never eat a click, and `aria-hidden` so it
// is decoration to a screen reader — which already knows the region scrolls.
//
// ── 133 §3 — WIDENED TO THE HORIZONTAL AXIS, NOT FORKED ────────────────────
//
// Laurent: "I asked that in all 14 days agenda views, there be a lateral
// scrolling, with arrows right and left showing it, like the arrows showing
// scrolling availability in the modals." He is naming THIS hook, so this hook
// is what grows an axis — a second copy of it would be the same measurement
// written twice, and the two would drift the first time either was corrected.
//
// Everything below is symmetric: `scrollTop`/`clientHeight`/`scrollHeight`
// gains `scrollLeft`/`clientWidth`/`scrollWidth`, the same 1px of slack, the
// same listeners, the same ResizeObserver. A scroller that overflows on one
// axis reports `false` on the other, so every existing mount — the 26 modals
// through ModalLayout — is unchanged by construction: `.modal-body` is
// `overflow-y: auto` and its `scrollWidth` never exceeds its `clientWidth`.
// That is an argument, and the drive is what makes it a measurement.
/* 151D §1(b) — `attachKey`: a scroller that MOUNTS LATER than the hook (the
   type-ahead's portalled menu exists only while open) re-runs the attach
   effect when the key changes, so the listeners land on the element that now
   exists. Absent for every existing mount — same single attach as before. */
export function useScrollHint(attachKey?: unknown) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [above, setAbove] = useState(false);
  const [below, setBelow] = useState(false);
  const [left, setLeft] = useState(false);
  const [right, setRight] = useState(false);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    // 1px of slack: sub-pixel layout makes an exactly-scrolled-to-bottom
    // element report scrollTop + clientHeight a fraction under scrollHeight,
    // which would leave the down arrow showing for ever at the end.
    setAbove(el.scrollTop > 1);
    setBelow(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
    setLeft(el.scrollLeft > 1);
    setRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    measure();
    el.addEventListener("scroll", measure, { passive: true });
    // The content of a modal body changes AFTER mount more often than not — a
    // taxonomy resolves, a section expands, a list loads — and a hint measured
    // only at mount would then be a lie. ResizeObserver on the scroller and on
    // its content child catches both axes of that.
    /* 210 §11 — THE OBSERVER'S READ AND WRITE MOVE TO THE NEXT FRAME. WebKit
       logged « ResizeObserver loop completed with undelivered notifications »
       while the column was open, and the census pinned THIS observer: the
       column body's hint (CompactColumn's `colBHint`) was the one observer
       delivered in the notice's frame — twice, the body and its sections, then
       the sections alone, the body's own change left undelivered. Its callback
       READ the scroller's geometry inside the delivery; the observer is the
       main window's while the column's body lives in the pop-out's document,
       so that read laid the pop-out out mid-delivery and changed the very sizes
       being observed. The callback now only asks the element's own window for
       its next frame and measures there (one request per frame); the scroll and
       resize listeners still measure at once. Measured: `drive-210.js
       --part=column`, WebKit — one notice before, none after. */
    const view = el.ownerDocument.defaultView;
    let frame = 0;
    const later = (): void => {
      if (!view) {
        measure();
        return;
      }
      if (frame) return;
      frame = view.requestAnimationFrame(() => {
        frame = 0;
        measure();
      });
    };
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(later);
      ro.observe(el);
      for (const c of Array.from(el.children)) ro.observe(c);
    }
    window.addEventListener("resize", measure);
    return () => {
      el.removeEventListener("scroll", measure);
      if (ro) ro.disconnect();
      if (frame && view) view.cancelAnimationFrame(frame);
      window.removeEventListener("resize", measure);
    };
  }, [measure, attachKey]);

  return { ref, above, below, left, right };
}

// BRIEF 91 \u00A71e \u2014 200% BIGGER, AND THE DOWN ARROW HAD TO BE PLACED TO BE BIGGER.
//
// THE SIZE. `FS.s9` \u2192 `FS.s27`, three times the previous size, which is what
// "200% bigger" is arithmetically. It is not as loud as that sounds: the glyphs
// are U+25B4 / U+25BE \u2014 the SMALL triangles \u2014 whose ink fills roughly a third of
// their em box, so 27px of font is about 9px of visible arrow. At 9px it was
// about 3px, which is the complaint. \u00A71e's constraints hold unchanged: `height:
// 0` (no layout space) and two arrows rather than a track (not a scrollbar).
//
// THE PLACEMENT, and this is the half that is not decoration. A `height: 0`
// block lays its line box out DOWNWARD from its content-box top. For the UP
// hint (`top: 0`) that draws INTO the scrollport, which is why that one has
// always been visible. For the DOWN hint (`bottom: 0`) the box top sits ON the
// scrollport's bottom edge, so the line box draws BELOW it and `overflow-y:
// auto` clips it \u2014 the \u22122px margin was the only part of it that could ever
// show. Tripling the font makes a clipped arrow no bigger.
//
// `translateY(-100%)` lifts that line box by its own height, entirely inside the
// scrollport, and a transform is exactly the tool for it: it moves paint and not
// layout, so the zero-height promise survives. The \u22122px margins stay as they
// were, cancelling the sub-pixel each span would otherwise contribute.
const HINT_BASE: CSSProperties = {
  position: "sticky",
  height: 0,
  display: "block",
  textAlign: "center",
  pointerEvents: "none",
  lineHeight: 1,
  zIndex: Z.stickyCell,
};

/* 133 §3 — THE HORIZONTAL HINT'S OWN BASE, and the asymmetry is a fact about
   where each one can live rather than a second design.

   The vertical pair rides INSIDE the scroller as its first and last children,
   because `.modal-body` IS the scroller and IS the flex child that carries
   `flex: 1 1 auto; min-height: 0` — there is no wrapper to hang anything on,
   and putting one there would change the DOM shape of every modal in the app.
   `position: sticky` plus `height: 0` is what buys that: pinned to the
   scrollport's own edges, costing no layout.

   A strip has a wrapper (`HScroll` below owns one), so the horizontal pair is
   absolutely positioned against it and is OUTSIDE the scroller entirely. That
   keeps the same two promises more simply than sticky would: it costs no
   layout at all, and it does not travel with the content it is describing —
   a sticky element inside a horizontally scrolling flex row would have to be
   a flex ITEM, which means a slot and, with `gap`, two extra gaps the strip's
   own `minWidth` arithmetic counts.

   Same disc, same glyphs' weight, same `pointer-events: none`, same
   `aria-hidden`. It is an affordance and not a control: nothing here is
   clickable, on either axis. */
const HINT_BASE_H: CSSProperties = {
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  display: "block",
  pointerEvents: "none",
  lineHeight: 1,
  zIndex: Z.stickyCell,
};

/* BRIEF 108 \u00A78c \u2014 LEGIBLE OVER ANY CONTENT. The bare glyph at opacity .55 in
   `var(--tm)` vanished against whatever scrolled under it (the circled \u25BE sat
   on the footer border). The glyph now rides a small solid disc \u2014 `var(--bc)`
   with a hairline `var(--br)` border \u2014 so it contrasts with the body's
   content by construction: the disc is the card colour, the content is on the
   card, and the pair never blends. The two promises of the block above are
   untouched: the OUTER span keeps `height: 0` (the disc paints through the
   line box's visible overflow \u2014 no layout space), and it is still two arrows,
   not a scrollbar. `top/bottom: 4` are sticky insets that float the disc a
   step off the scrollport edge instead of on it. */
const HINT_DISC: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 22,
  height: 22,
  borderRadius: 999,
  background: "var(--bc)",
  border: "1px solid var(--br)",
  fontSize: FS.s27,
  color: "var(--tm)",
};

/* 133 \u00A73 \u2014 the glyph pair \u25C2 \u25B8 joins \u25B4 \u25BE. Same family (U+25B4/BE \u2194 U+25C2/B8:
   the SMALL triangles), so the four are one vocabulary at one weight, and a
   reader who has learnt "a disc with a triangle means there is more that way"
   learns nothing new. */
const HINT_GLYPH: Record<"up" | "down" | "left" | "right", string> = {
  up: "\u25B4",
  down: "\u25BE",
  left: "\u25C2",
  right: "\u25B8",
};

/* 151D §2(d)/§1(h) — `inset`: extra distance off the scrollport edge, for a
   scroller whose edge is occupied by ANOTHER sticky element (the te window's
   pinned column headers at the top, its counters pane at the bottom). Zero
   for every existing mount — the modals' hints render byte-identically. */
export function ScrollHint({ dir, inset = 0 }: { dir: "up" | "down" | "left" | "right"; inset?: number }) {
  /* 151D §2(d) — the down hint's lift MOVES TO THE DISC, on a measurement
     that refutes the block above: `translateY(-100%)` on the OUTER span is a
     percentage of its own border box, and that box is `height: 0` — the
     transform has always been a no-op, the line box drew BELOW the
     scrollport's bottom edge, and `overflow` clipped the disc to a ~5px
     sliver (driven in the te window: disc 1018.7–1045.8 against a 1023.6
     edge). Laurent's "the down arrow is missing" is this sliver. The inner
     disc HAS a height, so -100% of it is a real lift and the arrow lands
     entirely inside the scrollport — which is what the 91 §1e note always
     claimed the outer transform did. */
  const place: CSSProperties =
    dir === "up"
      ? { ...HINT_BASE, top: 4 + inset, marginBottom: -2 }
      : dir === "down"
        ? { ...HINT_BASE, bottom: 4 + inset, marginTop: -2 }
        : dir === "left"
          ? { ...HINT_BASE_H, left: 2 }
          : { ...HINT_BASE_H, right: 2 };
  return (
    /* `data-scroll-hint` is a seam and nothing else — the same kind this tree
       already carries at `data-drop-matter` and `data-te-provenance`. It is
       what lets 133 §3's drive prove RULE 2 ("widen, do not fork: the modals
       must behave identically afterwards") by reading the modals' own hints
       back, instead of matching on a glyph. Inert: no style, no behaviour. */
    <span aria-hidden="true" data-scroll-hint={dir} style={place}>
      <span style={dir === "down" ? { ...HINT_DISC, transform: "translateY(-100%)" } : HINT_DISC}>{HINT_GLYPH[dir]}</span>
    </span>
  );
}

/* 133 \u00A73 \u2014 THE HORIZONTAL SCROLLER, with the hints already in it.
 *
 * Both 14-day strips declare a `minWidth` wider than the column they sit in
 * (1159 and 1172, which is fourteen 80px columns plus their gaps), so their
 * last days were CLIPPED with no way to reach them \u2014 Laurent's screenshot.
 * This is the wrapper that makes them reachable, and it exists rather than
 * two copies of the same four lines because there are two strips and they
 * must not drift.
 *
 * Module scope, and hookless components stay hookless: it DOES hold a hook, so
 * it is a proper component (CLAUDE.md's Bug A \u2014 a `useState` inside a body-
 * local function is a remount per render, and the function-call shortcut is
 * not available to it).
 */
/* 193 §D — WIDENED, NOT FORKED (SESSION_RULES rule 2): the pro forma list
 * needs its horizontal scroller to be its VERTICAL window as well, because a
 * sticky header pins against the nearest scroll container and an
 * `overflow-x: auto` box is one in both axes (182 D.5's finding — the A–Z
 * separator that never pinned). So `maxHeight` makes this box the window
 * (`overflow: auto`, the te window's own shape, TimeEntryTable.tsx), the
 * up/down hints ride inside it as its first and last children (the idiom's
 * contract, `useScrollHint`'s own comment), `hintInsets.up` lifts the up
 * arrow under a pinned header, and `scrollerRef` hands the box to the
 * measure that sizes it (hooks/useWindowMax.ts). Every existing mount passes
 * none of the three and renders byte for byte what it did. */
export function HScroll({
  children,
  style,
  maxHeight,
  scrollerRef,
  hintInsets,
}: {
  children: ReactNode;
  style?: CSSProperties;
  maxHeight?: number | string;
  scrollerRef?: MutableRefObject<HTMLDivElement | null>;
  hintInsets?: { up: number; down: number };
}) {
  const hint = useScrollHint();
  const windowed = maxHeight != null;
  const setScroller = (el: HTMLDivElement | null) => {
    (hint.ref as MutableRefObject<HTMLDivElement | null>).current = el;
    if (scrollerRef) scrollerRef.current = el;
  };
  return (
    <div style={{ position: "relative" }}>
      {/* ── 147 §1.3 — ONE PIXEL OF TRAILING ROOM, AND IT IS A MEASUREMENT ──
          A scrollport whose content ends EXACTLY at its clip edge has no room
          for its content's own last border, because the scroller cannot reach
          its own end under a fractional root `zoom`. Measured in isolation at
          16/13: `scrollLeft` stops at 258.375 of a possible 259, short by 0.62
          CSS px, and the 14-day strip's last day then ends 0.766 visual px
          outside the clip. In the app, at 1280×800, the same reading is 0.00 /
          0.00 / 0.05 / 0.20 visual px across Petit · Moyen · Grand · Très
          grand — zero exactly where the zoom is 1 or where the strip does not
          scroll at all, and growing with the zoom everywhere else. That is
          142 §1's family: a length declared in CSS px meeting a coordinate
          space that is not in CSS px.

          ONE CSS PIXEL, DERIVED RATHER THAN TUNED. The residue is a SNAP of
          `scrollLeft` onto the device-pixel grid, so it is bounded by one
          device pixel; one CSS pixel is ≥ one device pixel at every zoom step
          where this scroller scrolls at all (`large` 1.08, `xlarge` 1.23 —
          `small` is 0.92 but there the strip fits and there is no residue to
          cover). Measured after: 0.00 at all four steps, on both strips.
          NOT two pixels: 2px measured WORSE than 1px in the isolation lab
          (0.227 at 16/13), because it overshoots into another snapping bucket
          — which is the tell that this is sub-pixel arithmetic and that a
          bigger number is not a safer one.

          WHAT IT COSTS, measured rather than waved away: one CSS pixel of the
          scrollport's content box. Where the content overflows, that is one
          pixel of extra scroll extent (`scrollWidth` 1172 → 1173) and nothing
          visible. Where it FITS — Petit, where the strip is narrower than its
          column — the row has 1px less to grow into, and the day column reads
          80.14 visual px instead of 80.20. The hints below are unaffected:
          they compare `scrollLeft + clientWidth` against `scrollWidth`, and
          both moved by the same pixel.

          ── 148 §3.2 — RE-EXAMINED ON ITS OWN, AND IT DOES NOT EARN ITS PLACE.
          §1.3 was written believing it was fixing the reported defect. It was
          not: 148 §3.1 establishes that the report is about TODAY'S cell,
          which moves through the fourteen, and this pixel is about the LAST
          one, which does not. So this fixed a defect nobody had reported, and
          the question §3.2 asks is whether it should stand.

          MEASURED BOTH WAYS, this build against a control build with the line
          removed — last day's right edge vs the clip, visual px, positive =
          outside (§1.3's own metric, so the numbers are comparable):

                        Petit    Moyen    Grand    Très grand
              without   -0.406   +0.000   +0.344   +0.125
              with      -1.406   -1.000   -0.656   -1.875

          So what it removes is AT MOST 0.344 visual px, at Grand — under one
          device pixel at dpr 1 and under 0.7 at dpr 2, which is antialiasing
          rather than a border anyone can see clipped. At Moyen it removes
          exactly nothing and at Petit there was nothing to remove. Its cost is
          the 1px stated above (`scrollWidth` 1159 → 1160 on the matter-list
          strip, confirmed).

          IT STANDS ANYWAY, AND THE REASON IS THE MISSING HALF OF THE
          MEASUREMENT. §3.2 asked for BOTH ENGINES, because the trailing
          padding of an `overflow-x: auto` scrollport is not treated the same
          way in WebKit — and WebKit will not launch on this box (20+ host
          libraries absent, no passwordless sudo). Laurent reads this app in
          Safari. Removing a shipped, green line on the strength of a
          measurement that does not cover the engine it was questioned for
          would be approximating; keeping it costs one measured, invisible
          pixel. RECOMMENDED: revert it if one glance in Safari shows the last
          day's border intact without it. That glance is the whole decision and
          it is not one this box can take. */}
      {/* ── 151C §3 — ONE PIXEL OF BOTTOM ROOM, THE VERTICAL SIBLING OF §1.3'S
          TRAILING PIXEL, and the measurement DISAGREES with the brief's
          hypothesis (a Dashboard-specific clipping ancestor): driven at
          1280×800 on tenant 1 (zoom 1.2307), NO ancestor clips the Dashboard
          cell — the detail-card holds 14.6 visual px of its own padding
          below the strip — and the "OK" matter-list twin has the IDENTICAL
          vertical geometry: cell border-box bottom == scrollport clip bottom
          on BOTH strips, slack 0.00, padding-bottom 0. What both share is
          the exact-fit §1.3 names for the horizontal axis ("a scrollport
          whose content ends EXACTLY at its clip edge has no room for its
          content's own last border") — and vertically no pixel was ever
          reserved. At fractional zoom the bottom border is already ONE
          device pixel (computed 0.8125 layout px here): whether the clip
          edge's snap swallows it is engine- and offset-luck, which is how
          one strip reads cut in Laurent's Safari while its twin reads fine,
          and why neither shows it in headless Chromium. Same derivation as
          §1.3: one CSS px ≥ one device px at every zoom step; cost, one
          invisible pixel of strip height at all mounts. Safari itself will
          not launch on this box (§3.2's own record) — the glance is the
          proof this cannot manufacture. */}
      <div
        ref={windowed ? setScroller : hint.ref}
        style={windowed ? { overflow: "auto", maxHeight, paddingRight: 1, paddingBottom: 1, ...style } : { overflowX: "auto", paddingRight: 1, paddingBottom: 1, ...style }}
      >
        {windowed && hint.above && <ScrollHint dir="up" inset={hintInsets?.up ?? 0} />}
        {children}
        {windowed && hint.below && <ScrollHint dir="down" inset={hintInsets?.down ?? 0} />}
      </div>
      {hint.left && <ScrollHint dir="left" />}
      {hint.right && <ScrollHint dir="right" />}
    </div>
  );
}

// --- ModalLayout ---
// Standard modal wrapper: overlay, card, header (title + X), scrollable body,
// footer (cancel + save + optional danger).
//
// B.3 (BRIEF 09) widened ModalLayout to the UNION of what the 62 open-coded
// modal shells passed, so those shells can route through it without
// approximation (SESSION_RULES Rule 2/4). Every prop added below defaults so
// the pre-B.3 call sites render byte-identical.
// Props:
//   title (string|JSX) — header text (.MH > h3)
//   onClose (fn) — X button handler; also the overlay/esc dismiss target
//   onSave (fn) — save button handler (omit to hide save button)
//   onCancel (fn) — cancel handler, defaults to onClose
//   saveLabel / cancelLabel (string) — button labels (default "Save"/"Cancel")
//   saveDisabled (bool, default false) — forwarded to the Save button's disabled
//   footer (JSX) — replaces the entire default .MF when truthy; footer={null}
//     renders NO footer bar (distinct from undefined → default footer)
//   maxWidth / width / maxHeight (number|string) — sizing on the .modal shell.
//     114 §3 — maxWidth now WORKS: it is written as `--modal-max`, which
//     `.modal`'s own `max-width: min(92vw, var(--modal-max, 100vw)) !important`
//     reads, so the smaller of the two always wins. The sentence that stood
//     here ("width is needed to shrink below the 480px default; max-width:92vw
//     !important beats maxWidth alone") described the DEFECT and is withdrawn:
//     `width` is still the way to make a dialog WIDER than 480, maxWidth is now
//     the way to make it narrower, and a mount passing maxWidth >= 480 is
//     unchanged by arithmetic (the used width was already min(480, 92vw)).
//   shellStyle (CSSProperties) — 188 §A.1: merged onto the .modal shell AFTER
//     width / maxWidth / maxHeight, carrying verbatim what the nineteen
//     hand-rolled shells set beyond those three (measured: `display: "flex"`
//     + `flexDirection: "column"` at 4 sites, `overflow` "hidden" 2 / "auto"
//     1, `padding: 16` 1). An escape hatch rather than a prop per concern:
//     each value is one site's exact object, three of the four restate the
//     stylesheet's own `.modal` rules, and naming them would invent four
//     props for six sites. Absent at every pre-188 mount.
//   overlayStyle (CSSProperties) — 188 §A.1: merged onto the .modal-overlay
//     after `{ zIndex }`; one site (InvoiceView's doubles dialog) centres its
//     box with an inline flex declaration the class already makes. Absent at
//     every pre-188 mount.
//   stopShellClicks (bool, default false) — 188 §A.1: the .modal shell gets
//     `onClick={(e) => e.stopPropagation()}`, which 15 of the 19 hand-rolled
//     shells carried instead of (or as well as) an overlay guard. It is not
//     the same shape as closeOnOverlay's guard: the guard only decides
//     whether the OVERLAY closes, while the stop keeps a click inside the box
//     from reaching the host's ancestors and document-level click listeners
//     (TaskMatrix.tsx's outside-click close). Pair it with closeOnOverlay to
//     reproduce "overlay closes, box swallows" exactly.
//   zIndex (number) — inline z-index on the .OV overlay (stacked dialogs).
//     The convention (109 §1a): base overlay 100 · stacked dialog 300 ·
//     imperative pickers 400 (openOneDrivePicker mounts its own body root,
//     so it must clear whatever dialog summoned it).
//     204 §1 [RULED — a modal opened while a full-screen pane is open renders
//     ABOVE the pane]: while `PaneOpenContext` is true (My Bento / the
//     Eisenhower matrix, App's `headerOverlay`) the overlay takes the
//     pane-relative pair instead — `Z.paneModal` (pane + 2) for a base
//     dialog, `Z.paneModal2` (pane + 4) for one a mount stacks over it — and
//     provides the level to the pickers it hosts (`LiftedLevelContext`); the
//     drawers' own `DRAWER_Z + 2` / `+ 4`, computed once here for every mount.
//   closeOnOverlay (bool, default false) — opt-in overlay-click dismiss
//     (target===currentTarget guard). Default keeps today's no-gutter-dismiss.
//   escToClose (bool, default false) — component-level window Escape → close
//   showX (bool, default true) — render the header X button
//   closeDisabled (bool, default false) — busy-gate: disables the X and blocks
//     overlay/esc dismissal
//   hideHeader (bool, default false) — render no .MH at all (busy shells)
//   headerContent (JSX) — replace the default .MH (h3 + X) entirely
//   recordId (number | string | null) — 204 §G [Laurent: "maybe make the
//     internal app ID of persons visible"]: a saved row's internal id, shown
//     INSIDE the h3 after the title as a muted, selectable, monospace `#id`
//     (`--tm`, `user-select: text`); never a field, nothing edits it. The ten
//     person modals pass `form.id`; absent (a new row) nothing renders. Here
//     and not in each modal because the header is this component's: one
//     definition, ten mounts.
//   rawBody (bool, default false) — render children directly, skipping .MB
//   children — body content
export function ModalLayout({
  title,
  recordId,
  onClose,
  onSave,
  onCancel,
  saveLabel,
  cancelLabel,
  saveDisabled = false,
  footer,
  maxWidth,
  width,
  maxHeight,
  shellStyle,
  zIndex,
  overlayStyle,
  closeOnOverlay = false,
  stopShellClicks = false,
  escToClose = false,
  showX = true,
  closeDisabled = false,
  hideHeader = false,
  headerContent,
  rawBody = false,
  bodyClass,
  children,
}: {
  title?: any;
  recordId?: number | string | null;
  onClose?: any;
  onSave?: any;
  onCancel?: any;
  saveLabel?: any;
  cancelLabel?: any;
  saveDisabled?: boolean;
  footer?: any;
  maxWidth?: any;
  width?: any;
  maxHeight?: any;
  shellStyle?: CSSProperties;
  /** 204 §1 — typed (was `any`): every mount passes a figure or a `+ n` over one. */
  zIndex?: number;
  overlayStyle?: CSSProperties;
  closeOnOverlay?: boolean;
  stopShellClicks?: boolean;
  escToClose?: boolean;
  showX?: boolean;
  closeDisabled?: boolean;
  hideHeader?: boolean;
  headerContent?: any;
  rawBody?: boolean;
  /** 205 §2.2 — an extra class on `.modal-body`, for a shell whose INSET is
   *  its own rather than the house's. One caller: the after-login Daily
   *  Overview, the fifth pop-out of §2's ruling, which takes the ruled quarter
   *  over the modal shell's 12/16 without moving every modal in the app
   *  (`.modal-body.popup-pad`, App.css — all three width arms). */
  bodyClass?: string;
  children?: any;
}) {
  const cancel = onCancel || onClose;
  /* 188 §E [RULED 188 (4)] — THREE PROPS PRUNED, after every conversion, on the
     regenerated census's word (docs/187-elements-census/modal.md, read on
     e3d45a43: "props no call site passes — showCancel · dangerButton ·
     closeLabel"; 87 mounts, none passes them). Each was a default, so its
     removal renders every mount identically: `showCancel` defaulted true and
     gated the footer Cancel — always rendered, still rendered; `dangerButton`
     defaulted to no danger buttons — `dangers` was always `[]`, the map
     rendered nothing; `closeLabel` defaulted to the X icon — always the X,
     still the X. `maxHeight`, the fourth candidate 187 named, has five callers
     since A.2 and stays. */

  // B.3 — component-level Escape hook for shells whose per-state esc-chain
  // entry dies on consolidation. Opt-in: the 8 pre-B.3 call sites never pass
  // escToClose and keep relying on App.jsx's global esc chain.
  useEffect(() => {
    if (!escToClose) return;
    const onKey = (e: any) => {
      if (e.key === "Escape" && !closeDisabled) {
        e.stopPropagation();
        (onClose || onCancel)?.();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [escToClose, closeDisabled, onClose, onCancel]);

  // §9 — measured on the body element itself; a rawBody shell never mounts it.
  const scrollHint = useScrollHint();

  const mlStyle: any = {};
  if (width) mlStyle.width = width;
  /* BRIEF 114 §3 — `maxWidth` GOES THROUGH `--modal-max`, NOT THROUGH
     `max-width`. `.modal` carries `max-width: ... !important` (App.css) and an
     inline declaration cannot outrank a bang, so this prop was INERT at every
     one of its 43 call sites: they each named a width and every one of them
     rendered the house 480. The rule now reads `min(92vw, var(--modal-max,
     100vw))`, so the figure reaches it as a variable the bang carries.
     A number needs its unit spelled out — React appends `px` to known style
     properties, never to a custom one — and a string (`"90vw"`,
     `"min(1100px, 96vw)"`) is passed through as written.

     166 §C — and the number→px rule now lives in ONE place: `modalCap`
     (`lib/tokens.ts`), which the twenty open-coded `.modal` shells needed
     anyway (TypeScript refuses a custom property in a `style` literal, so
     each of them would otherwise carry its own cast). This is the same
     assignment it always was, through the helper. */
  if (maxWidth) Object.assign(mlStyle, modalCap(maxWidth));
  if (maxHeight) mlStyle.maxHeight = maxHeight;
  /* 188 §A.1 — `shellStyle` is merged AFTER the three computed keys, so a
     site's own object lands last and wins a collision; absent at every
     pre-188 mount, so `mlStyle` is exactly what it was there. */
  if (shellStyle) Object.assign(mlStyle, shellStyle);
  /* 188 §A.1 — the overlay's style: `ovBase` is the expression that stood in
     the attribute (`zIndex != null ? { zIndex } : undefined`); `overlayStyle`
     is merged over it when given. Absent at every pre-188 mount, so those
     render the same attribute or none. */
  /* 204 §1 [RULED] — over an open pane the overlay is lifted to the
     pane-relative pair (lib/paneStacking.ts); at the ordinary scale `level`
     IS `zIndex` and the attribute is what it was. */
  const paneOpen = useContext(PaneOpenContext);
  const level: number | undefined = paneOpen ? paneRungFor(zIndex) : zIndex;
  /* 205 §5 — WHILE THIS SHELL IS LIFTED OVER A PANE IT OWNS THE ESCAPE. The
     pane's own listener is on `document` and therefore fires BEFORE every
     `window` listener in this file and in the hotkey chain, so it used to
     close the pane out from under whatever dialog the key was meant for. It
     now asks `isAbovePane()` first, and this is what answers. Exactly the same
     condition that lifts the overlay's z — one fact, two consequences. */
  useAbovePane(paneOpen);
  const ovBase = level != null ? { zIndex: level } : undefined;
  const ovStyle = overlayStyle ? { ...ovBase, ...overlayStyle } : ovBase;

  // Phase 15.1 r5 — OV is a visual backdrop only by default; form modals
  // routed through ModalLayout (case edit, time entry, task, agenda, stage,
  // document, CC edit, client) do NOT dismiss on accidental gutter clicks.
  // Close paths: X button in MH + global Escape handler in App.jsx.
  // B.3 — closeOnOverlay opts a shell back into overlay-click dismissal
  // (import / error / preview dialogs that historically closed on the
  // backdrop), using the same target===currentTarget guard those shells used.
  // (204 §1 moved these seven lines ABOVE the return: inside the provider
  // element a `//` line is JSX TEXT, and it rendered as a text node.)
  /* ── 206 §8.5 [RULED — Laurent: "fix the surfaced items"] — THE DRAWERS'
     `DRAWER_Z + 4` PAINT HOLE, CLOSED WITH 204 §1's OWN RUNGS ──────────────

     THE HOLE. 204 §1 taught this shell to hand its level down so the PORTALLED
     pickers it hosts (MenuSelect, TypeaheadPicker, FieldSelect, TimeField —
     menus portalled to `document.body` at 201 / 1000) paint over it instead of
     under it. But it handed it down ONLY while a pane was open, because that
     was the case in front of it. A shell that names its OWN high level with no
     pane anywhere — every dialog a drawer raises, `DRAWER_Z + 4` = 9995 —
     provided `null`, so its pickers stayed at their own 201 and painted under
     the 9995 overlay they sit in. MEASURED COORDINATE: `DocumentBillingModal`
     mounts a `MenuSelect` (the billed entry's activity, :1183) and is mounted
     at `DRAWER_Z + 4` by `EmailDeepSearchDrawer` (:2179) — 9995 shell, 201
     menu.
     THE RULE IS 204 §1's, GENERALISED BY ONE WORD: a shell provides its level
     to the pickers it hosts whenever that level is ABOVE `.modal-overlay`'s own
     stylesheet value — which is precisely the test `paneRungFor` already
     applies to decide base-dialog from stacked-dialog. A pane makes this true
     by lifting; a drawer's dialog makes it true by declaring it. One condition,
     both causes.
     WHAT ELSE IT REACHES, censused rather than hoped: every `zIndex` a mount
     passes to this shell today is one of `Z.modal2` (300), `300`, `Z.folderPicker`
     (400), `CALL_LAYER_Z + 2`, `DRAWER_Z + 2` (9993) or `DRAWER_Z + 4` (9995) —
     ten mounts across seven files, ALL of them above 100, and every one had the
     same hole for the same reason. A stacked dialog at 300 hosting a 201 menu
     is the same defect one rung down. NOTHING AT THE ORDINARY SCALE MOVES: a
     shell passing no `zIndex`, or one at or below `Z.modalOverlay`, still
     provides `null`, and `useMenuLevel` then returns each picker's own figure
     exactly as before. */
  const ownLevel = level != null && level > Z.modalOverlay ? level : null;
  return (
    <LiftedLevelContext.Provider value={ownLevel}>
    <div
      className="modal-overlay"
      style={ovStyle}
      onClick={
        closeOnOverlay
          ? (e) => {
              if (!closeDisabled && e.target === e.currentTarget)
                (onClose || onCancel)?.();
            }
          : undefined
      }
    >
      <div
        className="modal"
        style={Object.keys(mlStyle).length ? mlStyle : undefined}
        /* 188 §A.1 — `stopShellClicks`: the shell stops click propagation,
           which is how 15 of the 19 hand-rolled shells kept a click inside the
           box from reaching the overlay's own handler, the host's ancestors and
           the document (TaskMatrix's outside-click listener). `undefined` when
           absent — React attaches nothing, as before. */
        onClick={
          stopShellClicks
            ? (e) => {
                e.stopPropagation();
              }
            : undefined
        }
      >
        {hideHeader ? null : headerContent ? (
          headerContent
        ) : (
          <div className="modal-header">
            <h3>
              {title}
              {recordId != null && recordId !== "" && (
                <span
                  className="mono"
                  style={{ marginLeft: 8, fontSize: FS.s10, fontWeight: 500, color: "var(--tm)", userSelect: "text", cursor: "text" }}
                  title={tImperative("common.internal_id", { fallback: "Internal id" })}
                >
                  #{String(recordId)}
                </span>
              )}
            </h3>
            {showX && (
              <button
                type="button"
                className="btn btn-ghost"
                onClick={onClose}
                disabled={closeDisabled}
                style={{ padding: "2px 6px" }}
              >
                <X size={I.s} />
              </button>
            )}
          </div>
        )}
        {rawBody ? (
          children
        ) : (
          // §9 — the hint rides INSIDE the existing scroller; see useScrollHint.
          <div className={bodyClass ? "modal-body " + bodyClass : "modal-body"} ref={scrollHint.ref}>
            {scrollHint.above && <ScrollHint dir="up" />}
            {children}
            {scrollHint.below && <ScrollHint dir="down" />}
          </div>
        )}
        {footer === null
          ? null
          : footer || (
              <div className="modal-footer">
                <CancelButton label={cancelLabel} onClick={cancel} />
                {onSave && (
                  <SaveButton
                    label={saveLabel}
                    onClick={onSave}
                    disabled={saveDisabled}
                  />
                )}
              </div>
            )}
      </div>
    </div>
    </LiftedLevelContext.Provider>
  );
}

// --- DrawerShell · DrawerRow · the drawers' counters and acts ---
//
// 194D §0.5 — EXTRACTED FROM EmailDeepSearchDrawer, MOVED AND NOT BETTERED, so
// the messages drawer (194D §E) wears the same overlay, the same stacking
// level, the same header and close, the same row container and the same
// counter and act dress — by extraction, never by copy (SESSION_RULES rule 3:
// moving and bettering are two commits). What is below is the e-mail drawer's
// own markup and its own comments where a move allows them to travel; what
// changed is only what a move requires — the title, the aria label and the
// close arrive as props, and `fmtHM`/the counter line take the count WORD
// instead of building the e-mail one. Nothing here positions, paints or
// dismisses in a new way.

/* 170 §1b-iii — this panel's stacking, named once. Everything the drawer
   raises above itself is written as an offset from it, so the relationship
   survives a change to either number. */
export const DRAWER_Z = Z.drawer;

interface DrawerShellProps {
  /* 183 D.3 — MOUNTED IN THE PAGE, not over it. MEASURED: this drawer insists
     on being an overlay — a fixed, inset-0 panel at DRAWER_Z with its own
     title and Close, closed by Escape — so the dashboard, which wants it
     simply always open inside its own card, widens it here rather than
     wrapping it: in-flow, a bounded height the list and preview scroll in,
     no header, no Escape. Everything else is the same component. */
  inline?: boolean;
  /** the overlay's own title (the inline mount sits under its host's header) */
  title: string;
  ariaLabel: string;
  /** the header's Close, and Escape's target */
  onClose: () => void;
  children?: ReactNode;
}

/**
 * The drawer's shell: a right-side slide-in panel for a long-range list — or,
 * inline, a bounded in-flow region under its host's header.
 */
export function DrawerShell({ inline = false, title, ariaLabel, onClose, children }: Readonly<DrawerShellProps>) {
  const { t } = useTranslation() as { t: Translate };
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  });
  // Close on Esc — an overlay's gesture; an inline mount has nothing to close.
  useEffect(() => {
    if (inline) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeRef.current();
    };
    globalThis.addEventListener("keydown", onKey);
    return () => {
      globalThis.removeEventListener("keydown", onKey);
    };
  }, [inline]);

  return (
    <>
      {/* 170 §1b-ii — THE BACKDROP IS GONE, and its click with it.
          It dimmed the app behind a panel that is the working surface, and —
          Laurent's (ii)(c), the one that costs him real time — a single stray
          click anywhere outside the drawer discarded the entire search and all
          work in progress.

          CHECKED BEFORE DELETING, as the brief required: the element carried
          NOTHING else. No focus trap (this drawer sets no tabIndex, manages no
          focus, declares no aria-modal), no scroll lock (nothing touches
          document.body.style.overflow anywhere in this file). Escape still
          closes — that is a window keydown listener above, independent of this
          element — and the header's own close control is unchanged. */}
      <aside
        role={inline ? "region" : "dialog"}
        aria-label={ariaLabel}
        style={inline ? {
          /* 183 D.3 — in the page's flow: a bounded CSS-px height (never vh —
             the 142 family) the list and the preview pane scroll inside, the
             host card's own border around it. */
          position: "relative",
          height: 640,
          border: "1px solid var(--br)",
          borderRadius: RAD.r8,
          background: "var(--bc)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        } : {
          position: "fixed",
          /* 170 §1b-i — FULL WIDTH. At `min(980px, 94vw)` an opened email's
             description column was truncated heavily; this panel is the
             working surface, not a peek at one. Expressed as left:0 + right:0
             rather than `width:100vw`, because this app runs inside a root
             CSS `zoom` and a vw length is measured against the VISUAL viewport
             — the 142 family of bugs. Two edges have no scale to get wrong.
             `borderLeft` goes with the width: at x=0 it was a line down the
             viewport edge. */
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "var(--bc)",
          boxShadow: "-8px 0 24px rgba(0,0,0,.35)",
          zIndex: DRAWER_Z,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header — the overlay's own title and Close; an inline mount sits
            under its host's header (183 D.3). */}
        {!inline && (
          <div
            style={{
              padding: "10px 14px",
              borderBottom: "1px solid var(--br)",
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <h3 style={{ margin: 0, fontSize: FS.s13 }}>{title}</h3>
            {/* 194D F.0 [RULED — Laurent: "app style and visible; now colour
                and styling are off"] — THE APP'S MODAL CLOSE: `btn btn-ghost`
                at `2px 8px`, the word `common.close` — the control
                AttachmentSaveModal's header carries, measured; the bare `.btn`
                that stood here read as plain text. Never a third style. */}
            <button
              type="button"
              className="btn btn-ghost"
              onClick={onClose}
              style={{ marginLeft: "auto", padding: "2px 8px" }}
            >
              {t("common.close", { fallback: "Close" })}
            </button>
          </div>
        )}
        {children}
      </aside>
    </>
  );
}

interface DrawerRowProps {
  /** the row's gap: the counters' 10, the filters' 8 */
  gap?: number;
  /** the counters and the filters wrap; the results header does not */
  wrap?: boolean;
  fontSize?: number;
  children?: ReactNode;
}

/** One row of the drawer under its search rows: bordered below, its items on
 *  one line, wrapping when it is told to. */
export function DrawerRow({ gap = 10, wrap = true, fontSize, children }: Readonly<DrawerRowProps>) {
  const style: CSSProperties = {
    padding: "6px 14px",
    borderBottom: "1px solid var(--br)",
    display: "flex",
    alignItems: "center",
    gap,
  };
  if (wrap) style.flexWrap = "wrap";
  if (fontSize != null) style.fontSize = fontSize;
  return <div style={style}>{children}</div>;
}

/** The save act's dress (the e-mail drawer's `archiveStyle`). */
export const DRAWER_ACT_SAVE_STYLE: CSSProperties = {
  height: 28,
  padding: "0 12px",
  fontSize: FS.s12,
  fontWeight: 600,
  background: "rgba(74,124,255,.12)",
  color: "var(--ac)",
  border: "1px solid rgba(74,124,255,.35)",
  borderRadius: RAD.r6,
  fontFamily: "inherit",
};
/** The bill act's dress — the e-mail drawer's `amberStyle` under its green
 *  overrides, which is what every bill button of the drawer rendered. */
export const DRAWER_ACT_BILL_STYLE: CSSProperties = {
  height: 28,
  padding: "0 12px",
  fontSize: FS.s12,
  fontWeight: 600,
  background: "rgba(52,211,153,.14)",
  color: "var(--gn)",
  border: "1px solid rgba(52,211,153,.4)",
  borderRadius: RAD.r6,
  fontFamily: "inherit",
};
export const DRAWER_COUNTER_STYLE: CSSProperties = {
  height: 26,
  padding: "0 8px",
  display: "inline-flex",
  alignItems: "center",
  fontSize: FS.s11,
  background: "var(--bd)",
  color: "var(--tp)",
  // 108 (d) — NO BORDER. A bordered, filled, rounded box is how this app
  // says "type here", and every value wearing this style is an OUTPUT:
  // "— emails" / "— messages", "0 selected", "0h 0m", the save progress.
  // They were borrowing the clothes of the per-row minutes cell, which is
  // the one thing in these panels that IS editable and keeps its border.
  // The monospace, the fill and the alignment stay — what goes is the
  // single signal that meant editable. `* { box-sizing: border-box }`
  // (App.css) means the outer height does not move, so nothing shifts.
  borderRadius: RAD.r6,
  fontFamily: "'JetBrains Mono',monospace",
  whiteSpace: "nowrap",
};

/* 194D D.5 [RULED — Laurent: "there is no contrast so invisible because of
   background"] — THE TOGGLE PAIRS GET A VISIBLE OUTLINE, IN THE DRAWERS ONLY.
   SegmentedControl is NOT changed and no other screen's toggles move: this
   is passed through its own `style` prop by the two drawers' instances. Why
   an outline and not a background: the halves paint their own ground inline
   (`--bc` unlit, `--as` lit) and the drawer's ground is `--bc`, so an unlit
   half is invisible against the panel except for its border — and `--br`,
   the border token, is all but invisible on every shipped theme (158 §6
   measured 1.10–1.22 : 1). The outline took `--ts`, the secondary text
   colour, whose contrast against `--bc` themeDerivation asserts as a pair;
   it lands between the text and the ground on every theme by construction.
   194E §F [RULED — Laurent, 2026-09-18: "the filters toggles edges color must
   be a bit subdued, now too strong"] — MEASURED against the drawer's ground
   (`--bc`) on the running staging, default theme / dark: `--br` 1.03 / 1.19 : 1
   (gone, as 158 §6 said), `--ts` 4.51 / 7.24 : 1 (the "too strong"), `--tm`
   3.21 / 3.67 : 1. The outline takes `--tm`, the muted text token — a tree
   token, no value mixed; visible on both themes where `--br` is not, quieter
   than `--ts` on both; and it is the ink SegmentedControl already gives its
   unlit words (`color: var(--tm)` below), so the edge reads in the same ink
   as the text inside it. Still passed through `style` by the two drawers
   alone; SegmentedControl itself is unchanged (194D D.5 stands). */
export const DRAWER_TOGGLE_STYLE: CSSProperties = {
  border: "1px solid var(--tm)",
};

/* 196 §E.2 — THE DRAWERS' LIST HEADER DRESS, one definition: the typographic
   part of the e-mails drawer's column header row (Phase 21.1's, the one 194D
   D.7 made sortable — small caps in the muted ink), MOVED here unchanged so
   the messages drawer's new header row and the two checkbox headers (« FACT »
   / « ENR ») wear it too. Layout (the grid, the sticky top, the border) stays
   each row's own. */
export const DRAWER_LIST_HEAD_STYLE: CSSProperties = {
  fontSize: FS.s9,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.3px",
  color: "var(--tm)",
};

/* 196 §E.1 — a CHECKBOX COLUMN'S HEADER CELL: the word (« FACT » / « ENR »)
   stacked over the column's select-all box, in the 22px cell the column
   already has — the box keeps the row's own left edge under the word, and the
   word, wider than the track by a hair at this size, spills into the grid's
   gap rather than moving a column. Both drawers. */
export const DRAWER_CHECK_HEAD_STYLE: CSSProperties = {
  display: "inline-flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 2,
  whiteSpace: "nowrap",
  lineHeight: 1,
};

/* 197 §B / §C — THE RESTATED ROWS' DRESS, one definition for both drawers: a
   FIGURE (the count that leads a row), the TEXT beside it, a NOTE (the quieter
   sentences at a row's tail), a LABEL (the filter row's words) and the
   period's grid on the filter row (the tail's own tracks, 194D E.2: 150 · 130
   · 130). */
export const DRAWER_ROW_FIGURE_STYLE: CSSProperties = { fontSize: FS.s11, fontWeight: 600, color: "var(--tp)", whiteSpace: "nowrap" };
export const DRAWER_ROW_TEXT_STYLE: CSSProperties = { fontSize: FS.s11, color: "var(--ts)" };
export const DRAWER_ROW_NOTE_STYLE: CSSProperties = { fontSize: FS.s10_5, color: "var(--tm)" };
export const DRAWER_ROW_LABEL_STYLE: CSSProperties = { fontSize: FS.s10, color: "var(--tm)", whiteSpace: "nowrap" };
export const DRAWER_PERIOD_GRID_STYLE: CSSProperties = { display: "grid", gridTemplateColumns: "auto 150px auto 130px auto 130px", columnGap: 6, alignItems: "center" };

/* 207 §10 (iii) [RULED — Laurent: "put dots between the info given in those
   lines (now just a supplementary space: looks sloppy)"] — THE FACTS OF ONE
   ROW, « · » BETWEEN THEM: « 450 messages trouvés · dans l'intervalle du … au
   … », « 3 messages facturables … · 00:00 h. entrantes · 33:53 h. sortantes ·
   13 553,33 € (honoraires et frais) au taux horaire de … ». Both drawers' fact
   rows wrap their facts in this one group — 207 §10 (ii): a change to one
   drawer's rows lands in the other — so the row's own gap (DrawerRow's 10)
   stands only between the group and what is not a fact: a title before it,
   a control after it. Every fact is an ELEMENT; an absent one (`false`,
   `null`) takes no dot. The dots are not read aloud. */
const DRAWER_FACTS_STYLE: CSSProperties = { display: "inline-flex", alignItems: "baseline", flexWrap: "wrap", rowGap: 2 };
const DRAWER_FACT_DOT_STYLE: CSSProperties = { fontSize: FS.s11, color: "var(--tm)", margin: "0 6px" };
export function DrawerFacts({ children }: Readonly<{ children?: ReactNode }>) {
  const facts = Children.toArray(children).filter(isValidElement);
  return (
    <span style={DRAWER_FACTS_STYLE}>
      {facts.map((c, i) => (
        <Fragment key={c.key}>
          {i > 0 && <span aria-hidden="true" style={DRAWER_FACT_DOT_STYLE}>·</span>}
          {c}
        </Fragment>
      ))}
    </span>
  );
}

interface AmountAtRateProps {
  t: Translate;
  amount: number | null;
  rate: number | null;
}
/** 197 C.4 — « {X} (honoraires et frais) au taux horaire de {rate} », the
 *  served rate its hover; a dash without a rate. 207 §10 (ii) — MOVED HERE
 *  from MessagesDrawer unchanged, and the e-mails drawer's own copy of the
 *  same span (its billable row) reads it too: one amount for both drawers. */
export function AmountAtRate({ t, amount, rate }: Readonly<AmountAtRateProps>) {
  const title = rate == null
    ? t("emailSearch.no_rate_title", { fallback: "Priced at your own rate once a matter is chosen" })
    : t("emailSearch.rate_title", { rate: fc(rate), fallback: `Your effective rate on this matter: ${fc(rate)}` });
  const text = rate == null || amount == null
    ? "—"
    : t("emailSearch.amount_at_rate", { amount: fc(amount), rate: fc(rate), fallback: `${fc(amount)} (fees and costs) at the hourly rate of ${fc(rate)}` });
  return <span style={DRAWER_ROW_TEXT_STYLE} title={title}>{text}</span>;
}

/** `Nh Mm` — the drawers' hours. */
export const fmtHM = (m: number): string => `${String(Math.floor(m / 60))}h ${String(m % 60)}m`;
/** `HH:MM` — 197 B.2 / C.4's spelling for the two durations of the billable
 *  row (« 01:30 entrantes »), beside `fmtHM`'s for the counters; one definition
 *  for both drawers. */
export const fmtHHMM = (m: number): string => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
/** count · hours · amount — the counters' one spelling; a dash where there is
 *  no rate. The count WORD is the caller's ("3 e-mails", "12 messages"). */
export function drawerCounterText(countWord: string, minutes: number, amount: number | null): string {
  const money = amount == null ? "—" : fc(amount);
  return `${countWord} · ${fmtHM(minutes)} · ${money}`;
}

// 157 §2 — THE APP'S ONE TEXT PROMPT.
//
// The brief's §2 premise was that every native dialog is "a yes/no gate before
// a destructive or semi-destructive act". Two are not: DocTrackingMatrix asks
// for a document-type NAME with window.prompt, which collects text and has no
// yes/no reading at all. The shared confirm cannot carry that, so this is the
// one new shell — built here, on ModalLayout, once, rather than open-coded at
// each of the two call sites.
//
// Submit is refused on an empty or whitespace-only value, which is what both
// callers checked for by hand after window.prompt returned.
export function PromptDialog({
  title,
  initialValue = "",
  placeholder,
  confirmLabel,
  onSubmit,
  onCancel,
}: {
  title?: ReactNode;
  initialValue?: string;
  placeholder?: string;
  confirmLabel?: ReactNode;
  onSubmit: (value: string) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState(initialValue);
  const trimmed = value.trim();
  const submit = () => {
    if (!trimmed) return;
    onSubmit(trimmed);
  };
  return (
    <ModalLayout
      title={title}
      onClose={onCancel}
      onSave={submit}
      saveDisabled={!trimmed}
      saveLabel={confirmLabel || t("common.save", { fallback: "Save" })}
      maxWidth={420}
      zIndex={Z.modal2}
    >
      <input
        className="field-input"
        autoFocus
        value={value}
        placeholder={placeholder}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            submit();
          }
        }}
        style={{ width: "100%", fontSize: FS.s12 }}
      />
    </ModalLayout>
  );
}

// Phase 39 (Corr, Section 2) — colleague attendee picker for the case-FREE
// meeting / poll / email modals: the full firm roster (pre-sorted alphabetically
// by the caller) laid out in TWO columns, none pre-checked, with an "All" toggle
// that selects/deselects everyone. `items` are { id, name, email, checked };
// `onToggle(index)` flips one and `onToggleAll(nextAllChecked)` sets them all.
// Stable module-scope identity (no inline definition in a render body).
export function ColleagueCheckGrid({ items = [], onToggle, onToggleAll }: { items?: any[]; onToggle?: any; onToggleAll?: any }) {
  const { t } = useTranslation();
  const allChecked = items.length > 0 && items.every((p) => p.checked);
  const someChecked = items.some((p) => p.checked);
  const selCount = items.filter((p) => p.checked).length;
  return (
    <div>
      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "3px 0 6px",
          marginBottom: 4,
          borderBottom: "1px solid var(--br)",
          fontSize: FS.s10_5,
          fontWeight: 600,
          color: "var(--tp)",
          cursor: "pointer",
        }}
      >
        <input
          type="checkbox"
          checked={allChecked}
          ref={(el) => { if (el) el.indeterminate = someChecked && !allChecked; }}
          onChange={() => onToggleAll && onToggleAll(!allChecked)}
        />
        <span style={{ flex: 1 }}>{t("calls.attendees.all", { fallback: "All" })}</span>
        <span style={{ color: "var(--tm)", fontSize: FS.s9_5, fontWeight: 500 }}>
          {t("calls.attendees.count", { count: selCount, fallback: `${selCount} selected` })}
        </span>
      </label>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          columnGap: 14,
          rowGap: 0,
          maxHeight: 240,
          overflowY: "auto",
        }}
      >
        {items.map((p, i) => (
          <label
            key={p.id}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "3px 0",
              fontSize: FS.s10_5,
              cursor: "pointer",
              minWidth: 0,
            }}
          >
            <input
              type="checkbox"
              checked={!!p.checked}
              onChange={() => onToggle && onToggle(i)}
            />
            <span
              style={{
                flex: 1,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={p.email ? `${p.name} · ${p.email}` : p.name}
            >
              {p.name}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}

// --- LoadedOnUse ---
/* 208 §1 — A PART THAT LOADS WHEN USED, AND SAYS SO WHEN IT CANNOT. `load` is
   an `onUse` loader (src/lib/loadOnUse.mjs); `children` draws with what it
   hands back. What has already arrived draws in the same frame; on the way, a
   screen (`inline`) shows « Loading… » and a window shows nothing yet. A load
   that fails shows its words — in place for a screen, in a small window of its
   own for a window, with `onClose`'s button (« Close » unless named) when
   there is one — and « Reload the page »: the browser keeps a module that
   failed for the life of the page (loadOnUse.mjs says how that was
   measured), so a reload is the one way back. Never a blank. */
export function LoadedOnUse<M>({
  load,
  children,
  inline = false,
  onClose,
  closeLabel,
}: Readonly<{
  load: OnUse<M>;
  children: RenderOnUse<M>;
  inline?: boolean;
  onClose?: () => void;
  closeLabel?: string;
}>) {
  const { t } = useTranslation() as { t: Translate };
  const [mod, setMod] = useState<M | undefined>(() => load.now());
  const [failed, setFailed] = useState<string | null>(null);
  useEffect(() => {
    if (mod !== undefined) return undefined;
    let live = true;
    load().then(
      (m) => {
        if (live) setMod(() => m);
      },
      (e: unknown) => {
        if (live) setFailed(e instanceof Error ? e.message : String(e));
      },
    );
    return () => {
      live = false;
    };
  }, [load, mod]);
  if (mod !== undefined) return <>{children(mod)}</>;
  if (failed === null)
    return inline ? (
      <div style={{ padding: 16, color: "var(--tm)", fontSize: FS.s12 }}>{t("common.loading", { fallback: "Loading…" })}</div>
    ) : null;
  const reload = () => {
    globalThis.location.reload();
  };
  const reloadWord = t("common.reload_page", { fallback: "Reload the page" });
  if (inline)
    return (
      <div role="alert" style={{ padding: 16, display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 10, fontSize: FS.s12, color: "var(--tp)" }}>
        <div>{failed}</div>
        <button type="button" className="btn btn-primary" onClick={reload}>
          {reloadWord}
        </button>
      </div>
    );
  return (
    <ModalLayout
      title={t("common.failed_to_load", { fallback: "Failed to load" })}
      onClose={onClose ?? reload}
      showX={!!onClose}
      maxWidth={440}
      footer={
        <div className="modal-footer">
          {onClose && <CancelButton label={closeLabel ?? t("common.close", { fallback: "Close" })} onClick={onClose} />}
          <SaveButton label={reloadWord} icon="↻" onClick={reload} />
        </div>
      }
    >
      <div role="alert" style={{ fontSize: FS.s12, color: "var(--tp)" }}>
        {failed}
      </div>
    </ModalLayout>
  );
}

// --- ListNotLoaded ---
/* 210 §1 — WHERE A LIST THAT COULD NOT LOAD WOULD HAVE BEEN. `lists` names the
   lists a host shows, counts or picks from (their `SK` keys); while every one
   of them is loaded this draws the host's own `children` and nothing else.
   When one is marked not loaded (src/lib/listLoad.ts says when), it says
   « La liste n’a pas pu être chargée » in their place — never « 0 », never an
   empty list — with « Réessayer », which asks for those lists alone (disabled
   while they are being asked). Three dresses: `block` in place of a section
   or a block, `inline` in place of a picker, `badge` in place of the
   sidebar's count — the words its title, the ask its click, and the click
   stays on the badge (the row under it navigates). 212 §22 — `words`: what
   is said when what failed is not a list (a section's read, the Microsoft 365
   status); the list's words otherwise. */
export function ListNotLoaded({
  lists,
  variant = "block",
  words: saying,
  children,
}: Readonly<{ lists: readonly string[]; variant?: "block" | "inline" | "badge"; words?: string; children?: ReactNode }>) {
  const { t } = useTranslation() as { t: Translate };
  const { notLoaded, asking, retry } = useContext(ListLoadContext);
  const failed = lists.filter((k) => Boolean(notLoaded[k]));
  if (!failed.length) return <>{children}</>;
  const busy = failed.some((k) => asking.has(k));
  const words = saying ?? t("common.list_load_failed", { fallback: "The list could not be loaded" });
  const again = t("calls.share.retry", { fallback: "Try again" });
  const ask = (e: React.MouseEvent) => {
    e.stopPropagation();
    retry(failed);
  };
  const mark = failed.join(" ");
  if (variant === "badge")
    return (
      <button
        type="button"
        className="badge"
        data-list-not-loaded={mark}
        title={words + " — " + again}
        aria-label={words + " — " + again}
        disabled={busy}
        onClick={ask}
        style={{ border: "none", cursor: busy ? "wait" : "pointer", color: "var(--rd)", fontWeight: 700 }}
      >
        !
      </button>
    );
  if (variant === "inline")
    return (
      <span
        role="alert"
        data-list-not-loaded={mark}
        style={{ display: "inline-flex", alignItems: "center", gap: 6, minWidth: 0, fontSize: FS.s11, color: "var(--rd)" }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{words}</span>
        <button
          type="button"
          className="btn btn-ghost"
          disabled={busy}
          onClick={ask}
          style={{ padding: "1px 8px", fontSize: FS.s11, flexShrink: 0 }}
        >
          {again}
        </button>
      </span>
    );
  return (
    <div
      role="alert"
      data-list-not-loaded={mark}
      style={{ padding: 16, display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 10, fontSize: FS.s12, color: "var(--tp)" }}
    >
      <div>{words}</div>
      <button type="button" className="btn btn-primary" disabled={busy} onClick={ask}>
        {again}
      </button>
    </div>
  );
}
